import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, QrCode, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { useAuth, useSpaceName } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ACCESS_STATE, BILL_STATUS, rupiah } from "@/lib/labels";

// Satu layar satu pekerjaan: sekolah melihat ke mana harus bayar, berapa, dan akun mana
// yang belum dilaporkan. Harga kulakan tidak pernah dikirim endpoint ini — hanya harga jual.
export default function SchoolLangganan() {
  const space = useSpaceName();
  const { me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sekolah", "langganan"], queryFn: () => fetcher("/school/langganan") });
  const [dipilih, setDipilih] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (q.isLoading) return <LoadingState rows={6} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const r = d.reseller;
  const bisaKlaim = !!me.school_permissions?.includes("billing:claim");
  const belum = d.tagihan.filter((b) => b.status === "PENDING");
  const dipilihIds = Object.keys(dipilih).filter((k) => dipilih[k]);

  const klaim = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/school/langganan/sudah-bayar", { bill_ids: dipilihIds });
      toast.success(`${data.diklaim} akun dilaporkan sudah dibayar. Reseller Anda akan mengonfirmasi.`);
      setDipilih({});
      await qc.invalidateQueries({ queryKey: ["sekolah", "langganan"] });
    } catch (ex) {
      setErr(errorMessage(ex));
    } finally {
      setBusy(false);
    }
  };

  const kanal = r
    ? [r.qris_label && `QRIS ${r.qris_label}`, r.qris_nmid, r.bank_account && `Rekening ${r.bank_account}`]
        .filter(Boolean).join(" · ")
    : "";

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={space} title="Langganan"
        description={`Tagihan langganan ${d.sekolah.name} periode ${d.periode}: ${rupiah(d.rekap.harus_bayar)} harus dibayar ke ${r?.name || "reseller Anda"} untuk ${d.rekap.akun} akun. Tidak ada faktur — laporkan sendiri setelah transfer.`} />

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-4" data-testid="school-billing-main">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border bg-card p-4">
              <p className="font-brand text-2xl font-extrabold tabular-nums">{d.rekap.akun}</p>
              <p className="text-xs text-muted-foreground">Akun ditagih</p>
            </div>
            <div className="rounded-2xl border bg-card p-4" data-testid="school-billing-belum">
              <p className="font-brand text-2xl font-extrabold tabular-nums text-[#E11D2E]">{d.rekap.PENDING}</p>
              <p className="text-xs text-muted-foreground">Belum bayar</p>
            </div>
            <div className="rounded-2xl border bg-card p-4">
              <p className="font-brand text-2xl font-extrabold tabular-nums text-emerald-600">{d.rekap.CLAIMED + d.rekap.CONFIRMED + d.rekap.ACTIVE + d.rekap.WAIVED}</p>
              <p className="text-xs text-muted-foreground">Sudah dilaporkan</p>
            </div>
          </div>

          {err && <Notice tone="danger" testId="school-billing-error">{err}</Notice>}
          {!r && <Notice tone="warn">Sekolah ini belum ditugaskan reseller aktif. Hubungi Pusat Kendali SIST.</Notice>}

          <div className="rounded-2xl border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
              <h2 className="text-base font-bold">Tagihan per akun</h2>
              {bisaKlaim && (
                <Button size="sm" onClick={klaim} disabled={busy || dipilihIds.length === 0}
                  data-testid="school-billing-claim-button">
                  {busy ? <Spinner /> : <BadgeCheck className="h-4 w-4 mr-1.5" />}
                  Tandai sudah bayar{dipilihIds.length ? ` (${dipilihIds.length})` : ""}
                </Button>
              )}
            </div>
            {d.tagihan.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground">Belum ada tagihan untuk sekolah Anda pada periode ini.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {bisaKlaim && <TableHead className="w-10" aria-label="Pilih" />}
                      <TableHead>Akun</TableHead>
                      <TableHead>Harga</TableHead>
                      <TableHead>Status bayar</TableHead>
                      <TableHead>Akses</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {d.tagihan.map((b) => (
                      <TableRow key={b.id}>
                        {bisaKlaim && (
                          <TableCell>
                            <Checkbox
                              checked={!!dipilih[b.id]}
                              onCheckedChange={(v) => setDipilih({ ...dipilih, [b.id]: !!v })}
                              disabled={b.status !== "PENDING"}
                              aria-label={`Tandai ${b.full_name || b.email} sudah bayar`}
                              data-testid={`school-billing-check-${b.user_id}`} />
                          </TableCell>
                        )}
                        <TableCell>
                          <p className="font-medium">{b.full_name || "—"}</p>
                          <p className="text-xs text-muted-foreground">{b.email} · {b.role}</p>
                        </TableCell>
                        <TableCell className="tabular-nums whitespace-nowrap">{rupiah(b.retail_price_idr)}</TableCell>
                        <TableCell><StatusBadge map={BILL_STATUS} value={b.status} /></TableCell>
                        <TableCell><StatusBadge map={ACCESS_STATE} value={b.access_state} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {bisaKlaim && d.rekap.PENDING > 0 && (
              <p className="px-4 py-3 text-xs text-muted-foreground border-t">
                Centang akun yang sudah Anda bayarkan, lalu tekan “Tandai sudah bayar”. Reseller Anda akan diminta
                mengonfirmasi; status baru berlaku setelah konfirmasi itu.
              </p>
            )}
          </div>
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border bg-card p-5 space-y-3" data-testid="school-billing-channel">
            <h2 className="flex items-center gap-2 text-base font-bold"><Wallet className="h-4 w-4 text-[#0B3D91]" aria-hidden /> Kanal pembayaran</h2>
            {r ? (
              <>
                <p className="text-sm text-muted-foreground">Bayar langsung ke {r.name} — mitra yang melayani sekolah Anda.</p>
                <dl className="space-y-2 text-sm">
                  <div><dt className="text-xs text-muted-foreground">Harga per akun per bulan</dt>
                    <dd className="font-medium" data-testid="school-billing-price">{rupiah(r.retail_price_idr)}</dd></div>
                  {kanal && <div><dt className="text-xs text-muted-foreground">Kanal</dt><dd className="font-medium break-words">{kanal}</dd></div>}
                </dl>
                {r.qris_image ? (
                  <div className="rounded-xl border p-3 grid place-items-center bg-muted/40" data-testid="school-billing-qris">
                    <img src={r.qris_image} alt={`QRIS ${r.name}`} className="max-h-56 rounded-lg" />
                  </div>
                ) : (
                  <div className="rounded-xl border p-4 grid place-items-center bg-muted/40 text-center" data-testid="school-billing-qris-empty">
                    <QrCode className="h-6 w-6 text-muted-foreground mb-1" aria-hidden />
                    <p className="text-xs text-muted-foreground">{r.name} belum memasang gambar QRIS. Gunakan kanal di atas.</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Belum ada reseller aktif yang melayani sekolah ini.</p>
            )}
          </section>
          <section className="rounded-2xl border bg-card p-5 space-y-2 text-sm text-muted-foreground">
            <h2 className="text-sm font-bold text-foreground">Cara kerja pelaporan</h2>
            <p>1. Transfer sesuai jumlah tagihan ke kanal di samping.</p>
            <p>2. Tandai akun yang sudah dibayar di tabel kiri.</p>
            <p>3. {r?.name || "Reseller"} mengonfirmasi; selama belum dikonfirmasi statusnya “sudah bayar ke reseller”.</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
